<template>
  <button
    v-capture-button-name="captureButtonName"
    type="button"
    :disabled="disabled"
    class="flex h-12 w-full items-center gap-1 rounded-sm px-3 py-3 text-left tracking-[-0.02em] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-grey focus-visible:ring-offset-2"
    :class="itemClasses"
    @click="handleClick"
  >
    <Icon v-if="icon" class="h-6 w-6" :icon="icon" :icon-style="IconStyle.Outline" />

    <span class="min-w-0 flex-1 truncate" :class="isActive ? 'typo-text2-bold' : 'typo-text2'">
      {{ label }}
    </span>

    <Icon v-if="outsideLink" class="h-5 w-5" :icon="IconName.NewWindow" :icon-style="IconStyle.Outline" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@kiplin/web-common/design-system';
import { IconName, IconStyle } from '@kiplin/web-common/icon';
import type { SideNavItemProps } from '@design-system/side-nav/application/component/SideNavItem/SideNavItem.types';

const props = withDefaults(defineProps<SideNavItemProps>(), {
  active: false,
});
const emit = defineEmits<{ click: [event: MouseEvent] }>();

const isActive = computed(() => props.active);

const itemClasses = computed(() => {
  if (isActive.value) {
    return 'border border-secondary bg-secondary-light text-black active:border-app-grey active:bg-app-grey-light';
  }

  return 'bg-app-white text-black hover:bg-app-grey-ultraLight active:border-app-grey active:bg-app-grey-light disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-grey disabled:opacity-50';
});

const handleClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  emit('click', event);

  if (event.defaultPrevented || !props.href) {
    return;
  }

  openLink({
    href: props.href,
    newTab: props.outsideLink,
  });
};

const openLink = ({ href, newTab }: { href: string; newTab: boolean }) => {
  if (newTab) {
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }

  window.open(href, '_self');
};
</script>
